import Button from '@atlaskit/button';
import EditIcon from '@atlaskit/icon/glyph/edit';
import { useFlaggingCallback } from '@databases/flags';
import { messages } from '@databases/intl/messages';
import usePanel from '@databases/panel/usePanel';
import { defaultStoreUtils } from '@databases/redux';
import { selectDatabaseDraftByContentId } from '@databases/redux/changesets/selectors';
import { SkeletonItem } from '@databases/skeleton';
import { SelectOption } from '@databases/types/database';
import { DatabaseViewSettings } from '@databases/types/database/view';
import { getGroupField } from '@databases/utils/database/view/view';
import React, { FunctionComponent } from 'react';
import { useIntl } from 'react-intl-next';
import styled from 'styled-components';

import { useScope } from '../../Scope';
import SelectOptionTag from '../../SelectOptionTag';
const Container = styled.div`
  display: flex;
  align-items: center;
  height: 24px;

  > :nth-child(2) {
    transition: opacity 0.4s;
    margin-left: 4px;
  }

  &:not(:hover, :focus-within) > :nth-child(2) {
    opacity: 0;
  }
`;

const EditButton = styled(Button)`
  svg {
    height: 16px;
    width: auto;
  }
`;

export interface BoardLayoutColumnHeaderProps {
  contentId: string;
  canEditFields: boolean;
  option: SelectOption | undefined;
  viewSettings: DatabaseViewSettings;
}

const BoardLayoutColumnHeader: FunctionComponent<BoardLayoutColumnHeaderProps> = ({
  contentId,
  canEditFields,
  option,
  viewSettings,
}) => {
  const scope = useScope();
  const { showPanel } = usePanel();
  const [groupFieldId] = defaultStoreUtils.useEqualSelector((state) => {
    const databaseDraft = selectDatabaseDraftByContentId(state, {
      contentId,
      scope,
      includeDeleted: true,
    });
    const groupField = getGroupField(databaseDraft, viewSettings.layout)!;
    return [groupField.id] as const;
  });

  const { formatMessage } = useIntl();

  const handleClick = useFlaggingCallback(async () => {
    showPanel('FieldSettingsPanel', { fieldId: groupFieldId });
  }, [showPanel, groupFieldId]);

  return (
    <Container>
      <SkeletonItem>
        <SelectOptionTag option={option} noMargin />
      </SkeletonItem>
      {canEditFields && (
        <EditButton
          iconBefore={
            <EditIcon label={formatMessage(messages['layout.board-edit-option-label'])} />
          }
          appearance="subtle"
          spacing="compact"
          onClick={handleClick}
        />
      )}
    </Container>
  );
};

export default BoardLayoutColumnHeader;
